<template>
  <v-form class="ade-forms-edit" @submit.prevent="submit" :key="id">
    <div class="ade-forms-edit__fields-wrapper">
      <v-container class="pb-0">
        <v-col>
          <h3 class="headline">
            {{
              isEdition ? $t("Modifier une société") : $t("Créer une société")
            }}
          </h3>
        </v-col>
        <div v-if="hasAdminRights">
          <v-col>
            <v-text-field
              v-model="nom"
              :label="$t('Agence*')"
              filled
              hide-details
              required
              @keyup="checkForm"
            />
          </v-col>
          <v-col>
            <v-text-field
              v-model="code"
              :label="$t('Code')"
              filled
              hide-details
              readonly
              disabled
            />
          </v-col>
          <v-col>
            <v-autocomplete
              v-if="!newSociete"
              v-model="societe"
              :label="$t(`Société*`)"
              :items="societeList"
              :search-input.sync="societeSearch"
              :loading="isSocieteSearchLoading"
              item-value="id"
              item-text="nom"
              no-filter
              append-icon="mdi-magnify"
              filled
              :hide-no-data="
                societeSearch == null ||
                  !societeSearch.length ||
                  isSocieteSearchLoading
              "
              clearable
              return-object
              :no-data-text="
                $t('Aucune société ne correspond à votre recherche...')
              "
              class="no-icon-rotation"
              hide-details
              required
              @change="checkForm"
            >
              <template v-slot:append-outer>
                <v-icon
                  key="icon-plus"
                  color="info"
                  @click="toggleNewSociete(true)"
                  v-text="'mdi-plus-circle'"
                />
              </template>
            </v-autocomplete>
            <v-text-field
              v-else
              v-model="societeNom"
              :label="$t(`Nouvelle société*`)"
              @click:clear="toggleNewSociete(false)"
              filled
              hide-details
              autofocus
              required
              @keyup="checkForm"
            >
              <template v-slot:append-outer>
                <v-icon
                  key="icon-cross"
                  color="info"
                  @click="toggleNewSociete(false)"
                  v-text="'mdi-close'"
                />
              </template>
            </v-text-field>
          </v-col>
          <v-col
            v-if="
              societe &&
                typeof societe !== 'undefined' &&
                Object.keys(societe).length !== 0
            "
          >
            <v-autocomplete
              v-if="!newFiliale"
              v-model="filiale"
              :label="$t(`Filiale*`)"
              :items="filialeList"
              :search-input.sync="filialeSearch"
              :loading="isFilialeSearchLoading"
              item-value="id"
              item-text="nom"
              no-filter
              :append-icon="newSociete || editFiliale ? 'mdi-magnify' : null"
              filled
              :hide-no-data="
                filialeSearch == null ||
                  !filialeSearch.length ||
                  isFilialeSearchLoading
              "
              :clearable="newSociete || editFiliale"
              return-object
              :no-data-text="
                $t('Aucune société ne correspond à votre recherche...')
              "
              class="no-icon-rotation"
              hide-details
              required
              @change="checkForm"
              :disabled="!newSociete && !editFiliale"
            >
              <template slot="selection" slot-scope="{ item }" v-if="item.id">
                {{ item.nom }}
              </template>
              <template slot="item" slot-scope="{ item }">
                {{ item.nom }}
              </template>
              <template v-if="newSociete || editFiliale" v-slot:append-outer>
                <v-icon
                  key="icon-plus"
                  color="info"
                  @click="toggleNewFiliale(true)"
                  v-text="'mdi-plus-circle'"
                />
              </template>
            </v-autocomplete>
            <div v-else>
              <v-text-field
                v-model="filialeNom"
                :label="$t(`Nouvelle filiale*`)"
                clearable
                @click:clear="toggleNewFiliale(false)"
                filled
                hide-details
                autofocus
                required
                @keyup="checkForm"
              />
            </div>
            <v-text-field
              v-if="filiale"
              v-model="filialeCodeMosi"
              :label="$t(`Code Mosi*`)"
              filled
              hide-details
              autofocus
              required
              :disabled="!editFiliale && !newFiliale"
              @keyup="checkForm"
            />
            <v-tooltip v-if="!newFiliale" bottom class="icon-expanded">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-on="on"
                  v-bind="attrs"
                  key="icon-lock"
                  :color="editFiliale ? 'info' : 'red'"
                  @click="toggleEditFiliale()"
                  v-text="editFiliale ? 'mdi-lock-open' : 'mdi-lock'"
                />
              </template>
              <span v-if="editFiliale">Vérouiller la filiale</span>
              <span v-else>Déverrouiller la filiale</span>
            </v-tooltip>
          </v-col>

          <v-col
            v-if="
              (societe && filiale && Object.keys(filiale).length !== 0) ||
                (!newSociete && societe)
            "
          >
            <v-autocomplete
              v-if="!newGroupe"
              v-model="computedGroupe"
              :disabled="!newFiliale"
              :label="$t(`Groupe`)"
              :items="groupeList"
              :search-input.sync="groupeSearch"
              :loading="isGroupeSearchLoading"
              item-value="id"
              no-filter
              :append-icon="newFiliale ? 'mdi-magnify' : null"
              filled
              :hide-no-data="
                groupeSearch == null ||
                  !groupeSearch.length ||
                  isGroupeSearchLoading
              "
              :clearable="newFiliale"
              return-object
              :no-data-text="
                $t('Aucune société ne correspond à votre recherche...')
              "
              class="no-icon-rotation"
              hide-details
            >
              <template slot="selection" slot-scope="{ item }" v-if="item.id">
                {{ item.nom }}
              </template>
              <template slot="item" slot-scope="{ item }">
                {{ item.nom }}
              </template>
              <template v-if="newFiliale" v-slot:append-outer>
                <v-icon
                  key="icon-plus"
                  color="info"
                  @click="toggleNewGroupe(true)"
                  v-text="'mdi-plus-circle'"
                />
              </template>
            </v-autocomplete>
            <v-text-field
              v-else
              v-model="groupeNom"
              :label="$t(`Nouveau groupe*`)"
              clearable
              @click:clear="toggleNewGroupe(false)"
              filled
              hide-details
              autofocus
            />
          </v-col>
        </div>
        <div v-else>
          <v-col v-if="!temporaire">
            <v-text-field
              v-model="nomComplet"
              :label="$t('Société*')"
              disabled
              filled
              hide-details
            />
          </v-col>
          <v-col v-else>
            <v-text-field
              v-model="nom"
              :label="$t('Société*')"
              filled
              hide-details
              required
            />
          </v-col>
        </div>
        <v-col>
          <v-autocomplete
            v-model="referent"
            :label="$t(`Personne référente • optionnel`)"
            :items="referentList"
            :search-input.sync="referentSearch"
            :loading="isReferentSearchLoading"
            item-value="id"
            no-filter
            append-icon="mdi-magnify"
            filled
            :hide-no-data="
              referentSearch == null ||
                !referentSearch.length ||
                isReferentSearchLoading
            "
            clearable
            return-object
            :no-data-text="
              $t('Aucune personne ne correspond à votre recherche...')
            "
            class="no-icon-rotation"
            hide-details
          >
            <template slot="selection" slot-scope="{ item }" v-if="item.id">
              {{ item.prenom }} {{ item.nom }}
            </template>
            <template slot="item" slot-scope="{ item }">
              {{ item.prenom }} {{ item.nom }}
              <span class="caption ml-1">({{ item.email }})</span>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col>
          <v-file-input
            v-model="file"
            accept="image/png, image/jpeg, image/bmp"
            :placeholder="$t('Sélectionner un fichier')"
            :label="$t('Logo')"
            prepend-icon="mdi-camera"
            :loading="fileLoading"
          >
            <template slot="append-outer" v-if="logo">
              <v-img
                :src="logo"
                :alt="logo"
                contain
                :max-width="32"
                :max-height="32"
              />
            </template>
          </v-file-input>
        </v-col>
      </v-container>
      <v-container class="pt-0">
        <v-subheader>
          {{ $t("Applications") }}
        </v-subheader>

        <v-col>
          <v-expansion-panels>
            <v-expansion-panel
              class="top-level-extansion-panel"
              active-class="extantion-panel-selected"
              v-for="(application, i) in applicationList"
              :key="i"
            >
              <v-expansion-panel-header>
                <v-checkbox
                  @click.native="expansionCheckboxClick($event)"
                  @change="toggleAllRightsByApplication(application, $event)"
                  :input-value="getAbonnementCheckboxState(application)"
                  :indeterminate="
                    getAbonnementCheckboxState(application) === -1
                  "
                  hide-details
                  class="mt-0 pt-0 mr-5"
                ></v-checkbox>
                {{ application.nom }}
                <!-- <v-icon class="icon-expanded" v-if="application.monoAgence">mdi-information-outline</v-icon> -->
                <v-tooltip
                  bottom
                  class="icon-expanded"
                  v-if="application.monoAgence"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      color="primary"
                      dark
                      v-bind="attrs"
                      v-on="on"
                      class="icon-expanded"
                    >
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <div>
                    <b>Mono-Agence</b> <br />
                    Application ne gérant les droits que d'une seule société
                  </div>
                </v-tooltip>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-col
                  v-for="field in application.configTemplate"
                  :key="field.slug"
                >
                  <v-menu
                    v-if="field.type == 'date'"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :label="field.label"
                        :value="
                          formatDate(
                            new Date(
                              getAbonnementsField(
                                application.id,
                                `config.${field.slug}`
                              )
                            )
                          )
                        "
                        filled
                        hide-details
                        append-icon="mdi-calendar"
                        readonly
                        clearable
                        v-on="on"
                      />
                    </template>
                    <v-date-picker
                      @change="
                        setAbonnementsField(
                          application.id,
                          `config.${field.slug}`,
                          new Date($event).toISOString()
                        )
                      "
                      locale="fr-FR"
                      first-day-of-week="1"
                      no-title
                    />
                  </v-menu>
                  <v-text-field
                    v-else-if="field.type == 'text'"
                    :label="field.label"
                    @change="
                      setAbonnementsField(
                        application.id,
                        `config.${field.slug}`,
                        $event
                      )
                    "
                    :value="
                      getAbonnementsField(
                        application.id,
                        `config.${field.slug}`
                      )
                    "
                    filled
                    hide-details
                  />
                  <v-container
                    v-else-if="
                      field.type == 'float' &&
                        checkFieldFilter(application.id, field)
                    "
                    class="container-flex"
                  >
                    <v-text-field
                      :label="field.label"
                      @change="
                        setAbonnementsField(
                          application.id,
                          `config.${field.slug}`,
                          $event
                        )
                      "
                      :value="
                        getAbonnementsField(
                          application.id,
                          `config.${field.slug}`
                        )
                      "
                      filled
                      hide-details
                      type="number"
                    />
                    <v-tooltip
                      bottom
                      class="icon-expanded"
                      v-if="application.monoAgence"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-if="field.slug == 'forfait_limite_taille'"
                          color="secondary"
                          class="send-request"
                          v-bind="attrs"
                          v-on="on"
                          :loading="loading"
                          @click="
                            addToken(application.id, `config.${field.slug}`)
                          "
                        >
                          <v-icon dark>mdi-send</v-icon>
                        </v-btn>
                      </template>
                      <div>Générer les tokens</div>
                    </v-tooltip>
                  </v-container>
                  <v-combobox
                    v-else-if="field.type == 'list'"
                    :label="field.label"
                    @change="
                      setAbonnementsField(
                        application.id,
                        `config.${field.slug}`,
                        $event
                      )
                    "
                    :value="
                      getAbonnementsField(
                        application.id,
                        `config.${field.slug}`
                      )
                    "
                    filled
                    hide-details
                  />
                  <v-container v-else-if="field.type == 'bool'">
                    <v-switch
                      :label="field.label"
                      :input-value="
                        getAbonnementsField(
                          application.id,
                          `config.${field.slug}`
                        )
                      "
                      @change="
                        setAbonnementsField(
                          application.id,
                          `config.${field.slug}`,
                          $event != null ? $event : false
                        )
                      "
                    ></v-switch>
                  </v-container>
                </v-col>
                <v-col v-if="hasConfigExternal(application.id)">
                  <v-text-field
                    :label="getConfigExternal(application.id).label"
                    :value="getConfigExternal(application.id).value"
                    readonly
                  />
                </v-col>
                <v-subheader>
                  {{ $t("Accès") }}
                </v-subheader>
                <v-expansion-panels multiple>
                  <v-expansion-panel
                    v-for="droit in application.droitsDisponible"
                    :key="droit.id"
                  >
                    <v-expansion-panel-header
                      :hide-actions="!droit.hasPerimetre"
                      :disabled="
                        !droit.hasPerimetre ||
                          !getAbonnementsAccesField(
                            application.id,
                            droit.id,
                            'used'
                          )
                      "
                    >
                      <v-checkbox
                        @click.native="expansionCheckboxClick($event)"
                        :color="getCheckboxColor(droit)"
                        @change="
                          setAbonnementsField(
                            application.id,
                            'acces',
                            {
                              droitId: droit.id,
                              used: $event
                            },
                            { array: true, toggle: $event }
                          )
                        "
                        :input-value="
                          getAbonnementsAccesField(
                            application.id,
                            droit.id,
                            'used'
                          )
                        "
                        hide-details
                        class="mt-0 pt-0 mr-5"
                      ></v-checkbox>
                      <span>
                        {{ droit.nom }}
                        <small
                          v-if="
                            droit.hasPerimetre &&
                              getAbonnementsAccesField(
                                application.id,
                                droit.id,
                                'used'
                              ) &&
                              !getAbonnementsMultiAccesField(
                                application.id,
                                droit.id,
                                'perimetre'
                              ).length
                          "
                          class="d-block grey--text"
                        >
                          {{ $t("Le périmètre n'a pas été renseigné") }}
                        </small>
                      </span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content
                      v-if="
                        droit.hasPerimetre &&
                          getAbonnementsAccesField(
                            application.id,
                            droit.id,
                            'used'
                          )
                      "
                    >
                      <v-autocomplete
                        :label="$t(`Périmètre`)"
                        @change.native="$event => ($event.target.value = '')"
                        @change="
                          updateAccesPerimetres(
                            application.id,
                            droit.id,
                            'perimetre',
                            $event
                          )
                        "
                        @focus="
                          updatePerimetreSearch(
                            $event,
                            application.id,
                            droit.perimetreTypeId
                          )
                        "
                        :placeholder="$t(`Chercher un périmètre...`)"
                        :items="
                          perimetreList.filter(
                            perimetre =>
                              !getAbonnementsMultiAccesField(
                                application.id,
                                droit.id,
                                'perimetre'
                              ).find(
                                selectedPerimetre =>
                                  selectedPerimetre.id == perimetre.id
                              )
                          )
                        "
                        @input.native="
                          updatePerimetreSearch(
                            $event,
                            application.id,
                            droit.perimetreTypeId
                          )
                        "
                        @blur="resetPerimetreSearch()"
                        :loading="isPerimetreSearchLoading"
                        no-filter
                        item-value="id"
                        item-text="nom"
                        append-icon="mdi-magnify"
                        filled
                        clearable
                        return-object
                        :hide-no-data="isPerimetreSearchLoading"
                        :no-data-text="
                          $t(
                            'Aucun périmètre ne correspond à votre recherche...'
                          )
                        "
                        class="no-icon-rotation"
                        hide-details
                      />
                      <div class="ade-forms-edit__chip-list">
                        <v-chip
                          v-for="perimetre in getAbonnementsMultiAccesField(
                            application.id,
                            droit.id,
                            'perimetre'
                          )"
                          :key="perimetre.id"
                          close
                          @click:close="
                            deleteAbonnementsMultiAccesByFieldValue(
                              application.id,
                              droit.id,
                              'perimetre',
                              perimetre,
                              'perimetre.id'
                            )
                          "
                          class="mt-2 ade-forms-edit__listable-chip"
                        >
                          {{ perimetre.nom }}
                        </v-chip>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-container>
    </div>

    <div class="ade-forms-edit-footer">
      <v-divider />
      <v-container class="d-flex justify-space-between">
        <v-btn color="grey" rounded depressed text @click="closeDrawer">
          {{ $t("Annuler") }}
        </v-btn>
        <create-button v-if="!isEdition">
          <template v-slot:default="{ on }">
            <v-btn
              color="primary"
              rounded
              depressed
              text
              :loading="loading"
              @click="submit({ callback: on })"
              :disabled="!formIsValid || (!hasRightValue && !isEdition)"
            >
              {{ $t("Créer et ajouter un autre") }}
            </v-btn>
          </template>
        </create-button>
        <v-btn
          type="submit"
          rounded
          color="primary"
          :loading="loading"
          depressed
          :disabled="!formIsValid || (!hasRightValue && !isEdition)"
        >
          {{ isEdition ? $t("Mettre à jour") : $t("Créer") }}
        </v-btn>
      </v-container>
    </div>
  </v-form>
</template>

<script>
import _ from "lodash";
import dateFormat from "dateformat";

import CreateButton from "@/components/companies/CreateButton";

import { createNamespacedHelpers } from "vuex";
import {
  FETCH,
  UPLOAD_LOGO,
  SUBMIT,
  UPDATE_ABONNEMENT,
  GENERATE_TOKEN,
  SEARCH
} from "@/store/action-types";
import { mapAppFields } from "@/store/modules/app";

import {
  mapCompanyCreateFormFields,
  mapCompanyCreateFormMultiRowFields,
  FIELD_SOCIETE,
  FIELD_FILIALE,
  FIELD_GROUPE
} from "@/store/modules/company";

const { mapGetters: mapAuthGetters } = createNamespacedHelpers(`auth`);

const { mapActions: mapCompanyActions } = createNamespacedHelpers(`company`);

const {
  mapGetters: mapCompanyCreateFormGetters,
  mapActions: mapCompanyCreateFormActions
} = createNamespacedHelpers(`company/createForm`);

const { mapState: mapApplicationState } = createNamespacedHelpers(
  `application`
);

const { mapActions: mapUserActions } = createNamespacedHelpers(`user`);
const { mapActions: mapPerimetreActions } = createNamespacedHelpers(
  `perimetre`
);

export default {
  components: {
    CreateButton
  },
  props: {
    company: {
      type: Object,
      required: false,
      default: () => null
    }
  },
  data: () => ({
    loading: false,
    referentSearch: null,
    isReferentSearchLoading: false,
    referentListBase: [],
    societeSearch: null,
    isSocieteSearchLoading: false,
    societeListBase: [],
    filialeSearch: null,
    isFilialeSearchLoading: false,
    filialeListBase: [],
    groupeSearch: null,
    isGroupeSearchLoading: false,
    groupeListBase: [],
    previousPerimetreSearch: null,
    perimetreSearch: null,
    isPerimetreSearchLoading: false,
    perimetreListBase: [],
    file: null,
    fileLoading: false,
    newSociete: false,
    newFiliale: false,
    editFiliale: false,
    newGroupe: false,
    upd: 0,
    hasRightValue: false,
    formIsValid: true
  }),
  computed: {
    ...mapAuthGetters(["hasAdminRights"]),
    isEdition() {
      return this.id !== null;
    },
    ...mapAppFields(["isCompanyDrawerOpen"]),
    ...mapApplicationState({
      applicationList: "list"
    }),
    // You can read more about mapping field
    // values in two of my previous articles.
    // https://markus.oberlehner.net/blog/form-fields-two-way-data-binding-and-vuex/
    // https://markus.oberlehner.net/blog/how-to-handle-multi-row-forms-with-vue-vuex-and-vuex-map-fields/
    ...mapCompanyCreateFormFields([
      "entity.id",
      "entity.nom",
      "entity.code",
      "entity.nomComplet",
      "entity.referent",
      "entity.logo",
      "entity.abonnements",
      "entity.temporaire",
      "entity.societeId",
      "entity.societe",
      "entity.societe.filiale",
      "entity.societe.filiale.groupe"
    ]),
    ...mapCompanyCreateFormFields({
      societeNom: "entity.societe.nom",
      filialeNom: "entity.societe.filiale.nom",
      filialeCodeMosi: "entity.societe.filiale.codeMosi",
      groupe: "entity.societe.filiale.groupe",
      groupeNom: "entity.societe.filiale.groupe.nom"
    }),
    computedGroupe: {
      get() {
        return this.filiale ? this.groupe : null;
      },
      set(value) {
        this.groupe = value;
      }
    },
    ...mapCompanyCreateFormMultiRowFields(["entity.abonnements"]),
    referentList() {
      let list = this.referentListBase || [];

      if (this.isEdition && this.referent && this.referent.id) {
        list = list.concat([this.referent]);
      }

      return list;
    },
    societeList() {
      let list = this.societeListBase || [];

      if (
        this.isEdition &&
        this.societe &&
        this.societe.id &&
        !list.includes(this.societe)
      ) {
        list = list.concat([this.societe]);
      }

      return list;
    },
    filialeList() {
      let list = this.filialeListBase || [];

      if (this.filiale && this.filiale.id && !list.includes(this.filiale)) {
        list = list.concat([this.filiale]);
      }

      return list;
    },
    groupeList() {
      let list = this.groupeListBase || [];

      if (
        this.computedGroupe &&
        this.computedGroupe.id &&
        !list.includes(this.computedGroupe)
      ) {
        list = list.concat([this.computedGroupe]);
      }

      return list;
    },
    selectedSociete: {
      get: function() {
        return this.societe ? this.societe : {};
      },
      set: function(value) {
        this.societe = value;
      }
    },
    getAbonnementCheckboxState: () =>
      function(application) {
        const abonnement = this._self.getAbonnementByApplicationId(
          application.id
        );

        if (abonnement && abonnement.acces) {
          const droits = abonnement.acces.map(droit => droit.used);
          const set = new Set(droits);

          if (!set.size) {
            return 0;
          }

          if (droits.length < application.droitsDisponible.length) {
            return -1;
          }

          return !set.has(true) ? 0 : !set.has(false) ? 1 : -1;
        } else {
          return 0;
        }
      },
    perimetreList() {
      let list = this.perimetreListBase || [];

      return list;
    },
    ...mapCompanyCreateFormGetters(["getAbonnementByApplicationId"])
  },
  updated() {
    this.checkForm();
  },
  methods: {
    checkForm() {
      //check agence
      let checkAgence = this.nom;
      //check societe
      let societeNom = this.societe && this.societe.nom ? this.societe.nom : "";
      let checkSociete = societeNom.trim().length > 0;
      //check Filiale
      let codeMosi =
        this.societe && this.societe.filiale && this.societe.filiale.codeMosi
          ? this.societe.filiale.codeMosi
          : "";
      let filialeNom =
        this.societe && this.societe.filiale && this.societe.filiale.nom
          ? this.societe.filiale.nom
          : "";
      let checkFilliale =
        codeMosi.trim().length > 0 && filialeNom.trim().length > 0;

      if (this.hasAdminRights) {
        this.formIsValid = checkAgence && checkSociete && checkFilliale;
      } else {
        this.formIsValid = checkAgence;
      }
    },
    hasRight() {
      let abonnements = this.abonnements;
      let reducer = (a, c) => {
        return [...a, ...c.acces];
      };
      let allAccesUsed = [];
      try {
        let allAcces = abonnements.reduce(reducer, []);
        allAccesUsed = _.filter(allAcces, function(a) {
          return a.used;
        });
      } catch (err) {
        allAccesUsed = [];
      }
      this.hasRightValue = allAccesUsed.length > 0;
    },
    ...mapUserActions({
      feupdateAbonnementstchUsers: FETCH
    }),
    ...mapPerimetreActions({
      fetchPerimetre: FETCH
    }),
    ...mapCompanyActions({
      submitAction: SUBMIT,
      uploadLogo: UPLOAD_LOGO,
      searchField: SEARCH
    }),
    ...mapCompanyCreateFormActions({
      updateAbonnements: UPDATE_ABONNEMENT,
      generateToken: GENERATE_TOKEN
    }),
    expansionCheckboxClick(e) {
      e.cancelBubble = true;
    },
    getAbonnementsField(applicationId, field = "") {
      return _.get(
        this.getAbonnementByApplicationId(applicationId),
        field,
        null
      );
    },
    checkFieldFilter(applicationId, field) {
      let result = true;
      try {
        let field_filter = `config.${field.field_filter.slug}`;
        let reverse = field.field_filter.reverse;

        result = _.get(
          this.getAbonnementByApplicationId(applicationId),
          field_filter,
          false
        );
        result = reverse ? !result : result;
      } catch (err) {
        //do nothing
      }
      return result;
    },
    hasConfigExternal(applicationId) {
      let field_filter = `configExternal`;
      let configExternal = _.get(
        this.getAbonnementByApplicationId(applicationId),
        field_filter,
        false
      );
      return configExternal != false;
    },
    getConfigExternal(applicationId) {
      let result = {};
      let field_filter = `configExternal`;
      let configExternal = _.get(
        this.getAbonnementByApplicationId(applicationId),
        field_filter,
        false
      );
      if (configExternal != null) {
        result.label = configExternal.label;
        result.value = configExternal.value;
      }
      return result;
    },
    getCheckboxColor(droit) {
      return droit.code == "bloque"
        ? "rgba(241, 59, 59, 0.8)"
        : "rgba(0, 188, 212, 0.8)";
    },
    getAbonnementsAccesById(applicationId, droitId) {
      return _.filter(
        this.getAbonnementsField(applicationId, "acces"),
        acces => acces.droitId == droitId
      );
    },
    getAbonnementsAccesField(applicationId, droitId, field) {
      return _.get(
        this.getAbonnementsAccesById(applicationId, droitId)[0],
        field,
        null
      );
    },
    setAbonnementsAccesField(applicationId, droitId, field, value) {
      return _.set(
        this.getAbonnementsAccesById(applicationId, droitId)[0],
        field,
        value
      );
      // this.updateAbonnementsAcces({ applicationId, field, value, array, toggle });
    },
    getAbonnementsMultiAccesField(applicationId, droitId, field) {
      let fields = [];

      this.getAbonnementsAccesById(applicationId, droitId).forEach(
        abonnement => {
          let value = _.get(abonnement, field, null);

          if (value) {
            fields.push(_.get(abonnement, field, null));
          }
        }
      );

      return fields;
    },
    updateAccesPerimetres(applicationId, droitId, field, value) {
      let hasPerimetre = !!this.getAbonnementsMultiAccesField(
        applicationId,
        droitId,
        "perimetre"
      ).length;

      if (!hasPerimetre) {
        this.deleteAbonnementsMultiAccesByFieldValue(
          applicationId,
          droitId,
          "perimetre",
          null
        );
      }

      this.addAbonnementsMultiAccesField(applicationId, droitId, field, value);
    },
    addAbonnementsMultiAccesField(applicationId, droitId, field, value) {
      // Add Acces only if some perimetre already exists otherwise update acces

      let addAcces = !!this.getAbonnementsMultiAccesField(
        applicationId,
        droitId,
        "perimetre"
      ).length;

      let valueToSave = {
        droitId: droitId,
        used: true,
        [field]: value
      };

      if (!addAcces) {
        valueToSave = [valueToSave];
      }

      this.setAbonnementsField(applicationId, "acces", valueToSave, {
        array: addAcces,
        add: addAcces
      });
    },
    deleteAbonnementsMultiAccesByFieldValue(
      applicationId,
      droitId,
      field,
      value,
      fieldToCompare
    ) {
      this.setAbonnementsField(
        applicationId,
        "acces",
        {
          droitId: droitId,
          used: true,
          [field]: value
        },
        { array: true, toggle: false, fieldToCompare }
      );
    },
    setAbonnementsField(
      applicationId,
      field = "",
      value,
      { array, toggle, add, fieldToCompare } = {}
    ) {
      this.updateAbonnements({
        applicationId,
        field,
        value,
        array,
        toggle,
        add,
        fieldToCompare
      }).finally(() => {
        this.hasRight();
      });
      this.$forceUpdate();
    },
    toggleAllRightsByApplication(application, toggle = true) {
      application.droitsDisponible.forEach(droit => {
        this.setAbonnementsField(
          application.id,
          "acces",
          {
            droitId: droit.id,
            used: toggle
          },
          { array: true, toggle }
        );
      });
    },
    async submit({ callback = () => {} }) {
      if (this.formIsValid) {
        this.$gtm.trackEvent({
          event: "interaction",
          category: "company",
          action: `Click (Envoie du formulaire société (${
            this.isEdition ? "Société existante" : "Nouvelle société"
          }))`,
          label: `Envoie du formulaire société (${
            this.isEdition ? "Société existante" : "Nouvelle société"
          })`
        });

        this.loading = true;
        await this.submitAction();
        this.loading = false;
        callback();
      }
    },
    updatePerimetreSearch(e, applicationId, perimetreTypeId) {
      let query = e.target.value;

      // JNA : ...&& this.previousPerimetreSearch!="" allows to trigger search each time focus event on input
      if (
        query == this.previousPerimetreSearch &&
        this.previousPerimetreSearch != ""
      ) {
        return;
      }

      this.previousPerimetreSearch = query;
      this.isPerimetreSearchLoading = true;

      this.fetchPerimetre({
        searchQuery: query,
        applicationId,
        perimetreTypeId
      }).then(perimetres => {
        if (perimetres) {
          this.perimetreListBase = perimetres;
          this.isPerimetreSearchLoading = false;
        }
      });
    },
    resetPerimetreSearch() {
      this.perimetreListBase = [];
    },
    closeDrawer() {
      this.isCompanyDrawerOpen = false;
    },
    addToken(applicationId, field = "") {
      //1 - set loading = true
      this.loading = true;

      //2 - get nbtoken
      let nbToken = this.getAbonnementsField(applicationId, field);

      //3 - generate Token
      this.generateToken({
        nbToken: nbToken,
        applicationId: applicationId
      })
        .then(res => {
          if (res.status == "200") {
            let field_filter = `configExternal.value`;
            let result = res.data.content;
            this.setAbonnementsField(applicationId, field_filter, result.value);
          } else {
            //TODO -
            //Oups, error occured
          }
        })
        .catch({
          //TODO  - MANAGE MESSAGE ERROR
          // this.message = this.$t("Une erreur a eu lieu. Veuillez réessayer.");
          // this.status = "error";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatDate(date) {
      if (date.getTime() != 0) {
        return dateFormat(date, "dd/mm/yyyy");
      }

      return null;
    },
    toggleNewSociete(toggle) {
      this.newSociete = toggle;
      this.societe = {
        filiale: null
      };
      if (!toggle) {
        this.toggleNewFiliale(false);
      }

      this.checkForm();
    },
    toggleNewFiliale(toggle) {
      this.newFiliale = toggle;
      this.filiale = {
        codeMosi: null,
        groupe: null
      };
    },
    toggleEditFiliale() {
      this.editFiliale = !this.editFiliale;
      if (this.editFiliale && this.filiale == null) {
        this.filiale = {
          groupe: null
        };
      }
    },

    toggleNewGroupe(toggle) {
      this.newGroupe = toggle;
      this.groupe = {};
    }
  },
  watch: {
    async referentSearch(query) {
      // Items have already been requested
      if (query === null) return;

      this.isReferentSearchLoading = true;

      await this.fetchUsers({
        temporary: true,
        search: { query }
      }).then(referents => {
        if (referents) {
          this.referentListBase = referents;
          this.isReferentSearchLoading = false;
        }
      });
    },
    async societeSearch(query) {
      // Items have already been requested
      if (query === null) return;

      this.isSocieteSearchLoading = true;

      await this.searchField({
        subfield: FIELD_SOCIETE,
        search: { query }
      }).then(societes => {
        if (societes) {
          this.societeListBase = societes;
          this.isSocieteSearchLoading = false;
        }
      });
    },
    societe(newSociete) {
      if (typeof newSociete !== "undefined") {
        this.societeId = newSociete.id;
      } else {
        this.societeId = null;
      }
    },
    async filialeSearch(query) {
      // Items have already been requested
      if (query === null) return;

      this.isFilialeSearchLoading = true;

      await this.searchField({
        subfield: FIELD_FILIALE,
        search: { query }
      }).then(filiales => {
        if (filiales) {
          this.filialeListBase = filiales;
          this.isFilialeSearchLoading = false;
        }
      });
    },
    async groupeSearch(query) {
      // Items have already been requested
      if (query === null) return;

      this.isGroupeSearchLoading = true;

      await this.searchField({
        subfield: FIELD_GROUPE,
        search: { query }
      }).then(groupes => {
        if (groupes) {
          this.groupeListBase = groupes;
          this.isGroupeSearchLoading = false;
        }
      });
    },
    // eslint-disable-next-line no-unused-vars
    async file(file) {
      if (file) {
        this.fileLoading = true;
        this.logo = await this.uploadLogo({ file });
        this.fileLoading = false;
      }
    },
    isCompanyDrawerOpen(toggle) {
      if (!toggle) {
        Object.assign(this.$data, this.$options.data());
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.icon-expanded {
  display: none;
}
.v-expansion-panel-header--active {
  .icon-expanded {
    display: block;
  }
}

.container-flex {
  display: flex;
}

.v-btn:not(.v-btn--round).v-size--default.send-request {
  height: auto;
}
</style>
